// src/components/Social/SocialFeed.jsx
//
// Feed real de publicaciones de contactos (homologacion con la app movil).
// Tercer paso: like y comentarios funcionales, stack de avatares de quien
// dio like, y lista de comentarios visible - mismos endpoints y misma
// logica que ya usa la app (POST /api/social/activities/:id/like y
// /comments), replicando el comportamiento completo, no solo el diseño.
import React, { useEffect, useState } from 'react';
import './SocialFeed.css';
import { MapContainer, TileLayer, Polyline } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import API_BASE_URL from '../../config/api';

import iconRunning from '../../assets/icons/running.png';
import iconCycling from '../../assets/icons/cycling.png';
import iconSwim from '../../assets/icons/swim.png';
import iconStrength from '../../assets/icons/strength.png';
import iconTriathlon from '../../assets/icons/triathlon.png';
import iconWalk from '../../assets/icons/walk.png';
import iconOther from '../../assets/icons/other.png';

function buildHeadersJson(userId) {
  const token = localStorage.getItem('token') || '';
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
  if (userId) headers['x-user-id'] = userId;
  return headers;
}

function resolvePhotoUrl(raw, scope = 'athlete') {
  if (!raw) return '';
  const v = String(raw).trim();
  if (!v) return '';
  if (/^data:image\//.test(v)) return v;
  if (/^https?:\/\//i.test(v)) return v;
  const host = API_BASE_URL.replace(/\/+$/, '');
  if (v.startsWith('/')) return `${host}${v}`;
  return `${host}/uploads/${scope}s/${v}`;
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('es-CL', { day: '2-digit', month: 'short' });
  } catch (_) {
    return '';
  }
}

function detectSportKey(post) {
  const t = String(post?.type || post?.title || '').toLowerCase();
  if (t.includes('run') || t.includes('correr') || t.includes('trote') || t.includes('trail')) return 'running';
  if (t.includes('bike') || t.includes('cycl') || t.includes('cicl') || t.includes('mtb')) return 'cycling';
  if (t.includes('swim') || t.includes('nadar') || t.includes('natac')) return 'swim';
  if (t.includes('strength') || t.includes('fuerza') || t.includes('gym') || t.includes('pesas')) return 'strength';
  if (t.includes('tri')) return 'triathlon';
  if (t.includes('walk') || t.includes('caminar') || t.includes('hike')) return 'walk';
  return 'other';
}

function sportIcon(key) {
  switch (key) {
    case 'running': return iconRunning;
    case 'cycling': return iconCycling;
    case 'swim': return iconSwim;
    case 'strength': return iconStrength;
    case 'triathlon': return iconTriathlon;
    case 'walk': return iconWalk;
    default: return iconOther;
  }
}

function Avatar({ src, name, size = 40 }) {
  const [failed, setFailed] = useState(false);
  const style = { width: size, height: size };
  if (failed || !src) {
    return (
      <div className="social-feed-avatar social-feed-avatar--fallback" style={style}>
        {String(name || 'T').slice(0, 1).toUpperCase()}
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={name || ''}
      className="social-feed-avatar"
      style={style}
      onError={() => setFailed(true)}
    />
  );
}

function LikersAvatarStack({ likers, likesCount }) {
  if (!likers || likers.length === 0 || !likesCount) return null;
  const visible = likers.slice(0, 3);
  const extra = likesCount - visible.length;
  return (
    <span className="social-feed-likers">
      {visible.map((liker) => (
        <span key={liker.userId} className="social-feed-likers-avatar" title={liker.name}>
          <Avatar src={resolvePhotoUrl(liker.photoUrl)} name={liker.name} size={20} />
        </span>
      ))}
      {extra > 0 && <span className="social-feed-likers-extra">+{extra}</span>}
    </span>
  );
}

function NoGpsHito() {
  return (
    <div className="social-feed-nogps">
      <div className="social-feed-nogps-pin"><span>TZ</span></div>
      <div className="social-feed-nogps-tail" />
      <span className="social-feed-nogps-label">Sin datos GPS</span>
    </div>
  );
}

const SEMI_TO_DEG = 180 / 2147483648;
function toDegMaybe(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return NaN;
  if (Math.abs(n) <= 180) return n;
  if (Math.abs(n) <= 2147483648) return n * SEMI_TO_DEG;
  return n;
}
function isValidLatLon(lat, lon) {
  return Number.isFinite(lat) && Number.isFinite(lon) && Math.abs(lat) <= 90 && Math.abs(lon) <= 180;
}

function PostRouteMap({ fileId, userId, isIndoor, onElevation }) {
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(!!fileId);

  useEffect(() => {
    let alive = true;
    if (!fileId || isIndoor) {
      setLoading(false);
      setPoints([]);
      if (onElevation) onElevation([]);
      return;
    }
    setLoading(true);
    fetch(`${API_BASE_URL}/api/fit/parsed-data/${encodeURIComponent(fileId)}`, {
      headers: buildHeadersJson(userId),
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!alive) return;
        const records = Array.isArray(data?.records) ? data.records : [];
        const coords = [];
        for (const r of records) {
          const lat = toDegMaybe(r?.position_lat ?? r?.lat ?? r?.latitude);
          const lon = toDegMaybe(r?.position_long ?? r?.position_lon ?? r?.lon ?? r?.lng ?? r?.longitude);
          if (isValidLatLon(lat, lon)) coords.push([lat, lon]);
        }
        setPoints(coords);
        const elevs = records
          .map((r) => r?.enhanced_altitude ?? r?.altitude ?? r?.elevation)
          .filter((e) => typeof e === 'number' && !Number.isNaN(e));
        if (onElevation) onElevation(elevs);
      })
      .catch(() => { setPoints([]); if (onElevation) onElevation([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [fileId, userId, isIndoor]);

  if (loading) return <div className="social-feed-map-loading">Cargando mapa...</div>;
  if (isIndoor || points.length < 2) return <NoGpsHito />;
  return (
    <div className="social-feed-map">
      <MapContainer center={points[0]} zoom={13} scrollWheelZoom={false} style={{ width: '100%', height: '100%' }}>
        <TileLayer attribution="&copy; OpenStreetMap contributors" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <Polyline positions={points} pathOptions={{ color: '#1878d6', weight: 4 }} />
      </MapContainer>
    </div>
  );
}

function ElevationChart({ series }) {
  if (!series || series.length < 2) return null;
  const step = Math.ceil(series.length / 120) || 1;
  const sampled = series.filter((_, i) => i % step === 0);
  const minE = Math.min(...sampled);
  const maxE = Math.max(...sampled);
  const range = maxE - minE || 1;
  const w = 300, h = 40;
  const pts = sampled.map((e, i) => {
    const x = (i / Math.max(1, sampled.length - 1)) * w;
    const y = h - ((e - minE) / range) * (h - 4);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  return (
    <div className="social-feed-elevation">
      <div className="social-feed-elevation-label">
        <span>Altimetria</span>
        <span>{Math.round(minE)}m — {Math.round(maxE)}m</span>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} width="100%" height={h} preserveAspectRatio="none">
        <polygon points={`0,${h} ${pts} ${w},${h}`} fill="#fbc775" fillOpacity="0.25" />
        <polyline points={pts} fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );
}

function CommentsList({ comments, userId }) {
  if (!comments || comments.length === 0) {
    return <p className="social-feed-no-comments">Aun no hay comentarios.</p>;
  }
  return (
    <div className="social-feed-comments-list">
      {comments.map((c) => (
        <div key={c.id || c.createdAt || c.text} className="social-feed-comment">
          <Avatar src={resolvePhotoUrl(c.userPhoto)} name={c.userName} size={30} />
          <div className="social-feed-comment-body">
            <span className="social-feed-comment-author">{c.userName || 'Contacto'}</span>
            <span className="social-feed-comment-text">{c.text}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function FeedPostCard({ post, userId }) {
  const [elevation, setElevation] = useState([]);
  const [interaction, setInteraction] = useState(
    post.interaction || { likesCount: 0, commentsCount: 0, likedByMe: false, likedByPreview: [], comments: [] }
  );
  const [showComments, setShowComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const author = post.author || {};
  const sportKey = detectSportKey(post);
  const authorPhoto = resolvePhotoUrl(author.photoUrl);

  const handleLike = async () => {
    const liked = !interaction.likedByMe;
    const prev = interaction;
    setInteraction({
      ...interaction,
      likedByMe: liked,
      likesCount: Math.max(0, (interaction.likesCount || 0) + (liked ? 1 : -1)),
    });
    try {
      const res = await fetch(`${API_BASE_URL}/api/social/activities/${encodeURIComponent(post.activityId)}/like`, {
        method: 'POST',
        headers: buildHeadersJson(userId),
        body: JSON.stringify({ liked }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || `HTTP ${res.status}`);
      if (data.interaction) setInteraction(data.interaction);
    } catch (_) {
      setInteraction(prev);
    }
  };

  const handleSubmitComment = async () => {
    const text = commentText.trim();
    if (!text || submitting) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/social/activities/${encodeURIComponent(post.activityId)}/comments`, {
        method: 'POST',
        headers: buildHeadersJson(userId),
        body: JSON.stringify({ text }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || `HTTP ${res.status}`);
      if (data.interaction) setInteraction(data.interaction);
      setCommentText('');
      setShowComments(true);
    } catch (_) {
      // silencioso: si falla, el usuario puede reintentar
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="social-feed-card">
      <div className="social-feed-card-header">
        <Avatar src={authorPhoto} name={author.name} />
        <div>
          <div className="social-feed-author">{author.name || 'Usuario'}</div>
          <div className="social-feed-meta">{post.type || post.title} · {formatDate(post.date)}</div>
        </div>
      </div>

      {post.text && <p className="social-feed-text">{post.text}</p>}

      <PostRouteMap fileId={post.fileId} userId={userId} isIndoor={!!post.isIndoor} onElevation={setElevation} />

      <div className="social-feed-sport-row">
        <img src={sportIcon(sportKey)} alt="" className="social-feed-sport-icon" />
        <span className="social-feed-sport-name">{post.type || post.title || 'Actividad'}</span>
      </div>

      <div className="social-feed-metrics-grid">
        <div>
          <div className="social-feed-metric-label">Distancia</div>
          <div className="social-feed-metric-value">{post.distance ? `${Number(post.distance).toFixed(2)} km` : '-'}</div>
        </div>
        <div>
          <div className="social-feed-metric-label">Duracion</div>
          <div className="social-feed-metric-value">{post.duration || '-'}</div>
        </div>
        <div>
          <div className="social-feed-metric-label">Ritmo</div>
          <div className="social-feed-metric-value">{post.pace || '-'}</div>
        </div>
        <div>
          <div className="social-feed-metric-label">Elevacion</div>
          <div className="social-feed-metric-value">{post.elevation ? `+${Math.round(post.elevation)} m` : '-'}</div>
        </div>
      </div>

      <ElevationChart series={elevation} />

      <div className="social-feed-interaction">
        <button
          type="button"
          className={`social-feed-action ${interaction.likedByMe ? 'social-feed-action--active' : ''}`}
          onClick={handleLike}
        >
          ❤ {interaction.likesCount || 0}
        </button>
        <LikersAvatarStack likers={interaction.likedByPreview} likesCount={interaction.likesCount} />
        <button type="button" className="social-feed-action" onClick={() => setShowComments((v) => !v)}>
          💬 {interaction.commentsCount || 0}
        </button>
      </div>

      {(showComments || (interaction.comments || []).length > 0) && (
        <div className="social-feed-comments">
          <CommentsList comments={interaction.comments} userId={userId} />
          <div className="social-feed-comment-form">
            <input
              type="text"
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSubmitComment(); }}
              placeholder="Escribe un comentario..."
              maxLength={800}
              className="social-feed-comment-input"
            />
            <button
              type="button"
              className="social-feed-comment-submit"
              onClick={handleSubmitComment}
              disabled={submitting || !commentText.trim()}
            >
              Enviar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function SocialFeed({ userId }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    if (!userId) {
      setLoading(false);
      setError('No hay sesion activa.');
      return;
    }
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const res = await fetch(`${API_BASE_URL}/api/community/feed?limit=30`, { headers: buildHeadersJson(userId) });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.message || `HTTP ${res.status}`);
        if (alive) setPosts(Array.isArray(data.posts) ? data.posts : []);
      } catch (e) {
        if (alive) setError(e.message || 'No se pudo cargar el feed.');
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => { alive = false; };
  }, [userId]);

  if (loading) return <div className="social-feed-status">Cargando feed...</div>;
  if (error) return <div className="social-feed-status social-feed-status--error">{error}</div>;
  if (!posts.length) {
    return (
      <div className="social-feed-status">
        Todavia no hay publicaciones de tus contactos. Cuando alguien de tu
        red registre una actividad, va a aparecer aca.
      </div>
    );
  }

  return (
    <div className="social-feed-list">
      {posts.map((post) => (
        <FeedPostCard key={post.id} post={post} userId={userId} />
      ))}
    </div>
  );
}
